// API Client for FastAPI Backend
import axios from 'axios';
import { authService } from './auth';

// Base URL for API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_PREFIX = '/api/v1';

// Create axios instance
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await authService.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting auth token:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Helper to get safe error messages for users
const getSafeErrorMessage = (status, data) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // In production, return generic messages
  if (!isDevelopment) {
    switch (status) {
      case 401:
        return 'Authentication failed. Please login again.';
      case 403:
        return 'Access denied.';
      case 404:
        return 'Resource not found.';
      case 422:
        return 'Invalid input. Please check your data and try again.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  }
  
  // In development, show detailed errors
  let errorMsg = data?.detail || data?.message || 'Unknown error';
  
  // Handle Pydantic validation errors (array of error objects)
  if (Array.isArray(data?.detail)) {
    errorMsg = data.detail.map(e => 
      `${e.loc?.join('.') || 'field'}: ${e.msg}`
    ).join('; ');
  } else if (Array.isArray(data)) {
    errorMsg = data.map(e => 
      `${e.loc?.join('.') || 'field'}: ${e.msg}`
    ).join('; ');
  }
  
  return errorMsg;
};

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const safeMessage = getSafeErrorMessage(status, data);
      
      // Only log detailed errors in development
      if (isDevelopment) {
        console.error(`API Error [${status}]:`, safeMessage);
      }
      
      // Handle specific status codes
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          window.location.href = '/';
          break;
        case 403:
        case 404:
        case 422:
        case 500:
        default:
          // Errors are handled by components
          break;
      }
    } else if (error.request) {
      // Request made but no response
      if (isDevelopment) {
        console.error('No response from server. Please check your connection.');
      }
    } else {
      // Error setting up request
      if (isDevelopment) {
        console.error('Request error:', error.message);
      }
    }
    
    return Promise.reject(error);
  }
);

// API service methods
export const api = {
  // ========== User/Auth ==========
  users: {
    register: async (formData) => {
      const response = await apiClient.post('/users/register', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    
    getProfile: async () => {
      const response = await apiClient.get('/users/me');
      return response.data;
    },
    
    updateProfile: async (data) => {
      const response = await apiClient.put('/users/me', data);
      return response.data;
    },
    
    getOrders: async () => {
      const response = await apiClient.get('/users/me/orders');
      return response.data;
    },
  },
  
  // ========== Categories ==========
  categories: {
    list: async (params = {}) => {
      const response = await apiClient.get('/categories', { params });
      return response.data;
    },
    
    getTree: async () => {
      const response = await apiClient.get('/categories/tree');
      return response.data;
    },
    
    getById: async (id) => {
      const response = await apiClient.get(`/categories/${id}`);
      return response.data;
    },
  },
  
  // ========== Products ==========
  products: {
    getCatalog: async (params = {}) => {
      const response = await apiClient.get('/products/catalog', { params });
      return response.data;
    },
    
    getDetail: async (productId) => {
      const response = await apiClient.get(`/products/${productId}/detail`);
      return response.data;
    },
    
    list: async (params = {}) => {
      const response = await apiClient.get('/products', { params });
      return response.data;
    },
    
    calculatePrice: async (productId, quantity, subcategoryId = null) => {
      const params = { quantity };
      if (subcategoryId) params.subcategory_id = subcategoryId;
      const response = await apiClient.post(`/products/${productId}/calculate-price`, null, { params });
      return response.data;
    },
  },
  
  // ========== Cart ==========
  cart: {
    get: async () => {
      const response = await apiClient.get('/cart');
      return response.data;
    },
    
    addItem: async (item) => {
      const response = await apiClient.post('/cart/items', item);
      return response.data;
    },
    
    updateItem: async (itemId, quantity) => {
      const response = await apiClient.put(`/cart/items/${itemId}`, { quantity });
      return response.data;
    },
    
    removeItem: async (itemId) => {
      const response = await apiClient.delete(`/cart/items/${itemId}`);
      return response.data;
    },
    
    clear: async () => {
      const response = await apiClient.delete('/cart');
      return response.data;
    },
    
    sync: async (localCart) => {
      const response = await apiClient.post('/cart/sync', localCart);
      return response.data;
    },
    
    recalculatePrices: async () => {
      const response = await apiClient.post('/cart/recalculate-prices');
      return response.data;
    },

    // Whether cart lines can still be bought (works for guest carts too).
    checkAvailability: async (items) => {
      const response = await apiClient.post('/cart/availability', { items });
      return response.data;
    },
  },
  
  // ========== Orders ==========
  orders: {
    list: async (params = {}) => {
      const response = await apiClient.get('/orders', { params });
      return response.data;
    },
    
    getById: async (orderId) => {
      const response = await apiClient.get(`/orders/${orderId}`);
      return response.data;
    },
    
    create: async (orderData) => {
      const response = await apiClient.post('/orders', orderData);
      return response.data;
    },
    
    reorder: async (orderId) => {
      const response = await apiClient.post(`/orders/${orderId}/reorder`);
      return response.data;
    },
    
    track: async (orderId) => {
      const response = await apiClient.get(`/orders/${orderId}/tracking`);
      return response.data;
    },
  },
  
  // ========== Checkout ==========
  checkout: {
    createSession: async (checkoutData) => {
      const response = await apiClient.post('/checkout/session', checkoutData);
      return response.data;
    },
    confirmCardSetup: async (orderId) => {
      const response = await apiClient.post(`/checkout/confirm-card-setup/${orderId}`);
      return response.data;
    },
    markCheckoutIncomplete: async (orderId) => {
      const response = await apiClient.post(`/checkout/orders/${orderId}/mark-incomplete`);
      return response.data;
    },
  },

  // ========== Payments ==========
  payments: {
    getOrderForPayment: async (orderId) => {
      const response = await apiClient.get(`/payments/order/${orderId}`);
      return response.data;
    },
    
    validateStock: async (orderId) => {
      const response = await apiClient.post(`/payments/validate-stock/${orderId}`);
      return response.data;
    },
    
    createPaymentIntent: async (paymentData) => {
      const response = await apiClient.post('/payments/create-intent', paymentData);
      return response.data;
    },
    
    confirmPayment: async (orderId) => {
      const response = await apiClient.post(`/payments/confirm/${orderId}`);
      return response.data;
    },
  },

  // ========== Addresses ==========
  addresses: {
    list: async () => {
      const response = await apiClient.get('/me/addresses');
      return response.data;
    },
    
    getById: async (addressId) => {
      const response = await apiClient.get(`/me/addresses/${addressId}`);
      return response.data;
    },
    
    create: async (addressData) => {
      const response = await apiClient.post('/me/addresses', addressData);
      return response.data;
    },
    
    update: async (addressId, addressData) => {
      const response = await apiClient.put(`/me/addresses/${addressId}`, addressData);
      return response.data;
    },
    
    delete: async (addressId) => {
      await apiClient.delete(`/me/addresses/${addressId}`);
    },
  },

  // ========== Shipping ==========
  shipping: {
    getQuote: async (quoteData) => {
      const response = await apiClient.post('/shipping/quote', quoteData);
      return response.data;
    },
  },

  // ========== Resale Certificates ==========
  resaleCertificates: {
    // Get current user's tax exemption certificates only
    // Backend automatically filters by authenticated user's ID
    list: async () => {
      const response = await apiClient.get('/resale-certificates');
      return response.data;
    },

    // Admin: get certificates for a specific user
    listByUser: async (userId) => {
      const response = await apiClient.get('/resale-certificates', { params: { user_id: userId } });
      return response.data;
    },
    
    // Get a specific certificate (must belong to current user)
    getById: async (certificateId) => {
      const response = await apiClient.get(`/resale-certificates/${certificateId}`);
      return response.data;
    },
    
    // Upload certificate file to Firebase Storage (private)
    uploadFile: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/upload/resale-certificate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    
    // Get secure signed URL to download certificate (admin only)
    getDownloadUrl: async (certificateId) => {
      const response = await apiClient.get(`/resale-certificates/${certificateId}/download-url`);
      return response.data;
    },
    
    // Create a new tax exemption certificate for current user
    create: async (certificateData) => {
      const response = await apiClient.post('/resale-certificates', certificateData);
      return response.data;
    },
    
    // Update certificate status (admin only)
    update: async (certificateId, certificateData) => {
      const response = await apiClient.put(`/resale-certificates/${certificateId}`, certificateData);
      return response.data;
    },
    
    // Delete a certificate (must belong to current user or be admin)
    delete: async (certificateId) => {
      await apiClient.delete(`/resale-certificates/${certificateId}`);
    },
  },

  // ========== Stock Notifications ==========
  stockNotifications: {
    list: async (includeNotified = false) => {
      const response = await apiClient.get('/stock-notifications', {
        params: { include_notified: includeNotified }
      });
      return response.data;
    },
    
    create: async (notificationData) => {
      const response = await apiClient.post('/stock-notifications', notificationData);
      return response.data;
    },
    
    delete: async (notificationId) => {
      await apiClient.delete(`/stock-notifications/${notificationId}`);
    },
    
    // Admin endpoints
    listAll: async (productId = null, includeNotified = false) => {
      const response = await apiClient.get('/stock-notifications/admin/all', {
        params: { 
          product_id: productId,
          include_notified: includeNotified 
        }
      });
      return response.data;
    },
    
    listByCustomer: async (customerId, includeNotified = false) => {
      const response = await apiClient.get(`/stock-notifications/admin/by-customer/${customerId}`, {
        params: { include_notified: includeNotified }
      });
      return response.data;
    },
    
    checkStockStatus: async () => {
      const response = await apiClient.post('/stock-notifications/admin/check-stock-status');
      return response.data;
    },
  },
  
  // ========== Homepage Assets ==========
  homepage: {
    getAssets: async () => {
      const response = await apiClient.get('/homepage-assets');
      return response.data;
    },
    
    getAllAssets: async () => {
      const response = await apiClient.get('/homepage-assets/admin/all');
      return response.data;
    },
    
    create: async (assetData) => {
      const response = await apiClient.post('/homepage-assets', assetData);
      return response.data;
    },
    
    update: async (assetId, assetData) => {
      const response = await apiClient.put(`/homepage-assets/${assetId}`, assetData);
      return response.data;
    },
    
    delete: async (assetId) => {
      await apiClient.delete(`/homepage-assets/${assetId}`);
    },
    
    batchReorder: async (items) => {
      const response = await apiClient.post('/homepage-assets/batch-reorder', { items });
      return response.data;
    },
    
    uploadHeroImage: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/upload/hero-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
  },

  // ========== Footer Pages (CMS) ==========
  footerPages: {
    list: async () => {
      const response = await apiClient.get('/footer-pages');
      return response.data;
    },
    getBySlug: async (slug) => {
      const response = await apiClient.get(`/footer-pages/${slug}`);
      return response.data;
    },
    listAdmin: async () => {
      const response = await apiClient.get('/footer-pages/admin/all');
      return response.data;
    },
    update: async (slug, data) => {
      const response = await apiClient.put(`/footer-pages/admin/${slug}`, data);
      return response.data;
    },
  },

  // ========== Site Content (admin-editable storefront text) ==========
  siteContent: {
    get: async (key) => {
      const response = await apiClient.get(`/site-content/${key}`);
      return response.data;
    },
    update: async (key, values) => {
      const response = await apiClient.put(`/site-content/admin/${key}`, { values });
      return response.data;
    },
  },

  // ========== Events ==========
  events: {
    list: async () => {
      const response = await apiClient.get('/events');
      return response.data;
    },
    getById: async (eventId) => {
      const response = await apiClient.get(`/events/${eventId}`);
      return response.data;
    },
    listAdmin: async () => {
      const response = await apiClient.get('/events/admin/all');
      return response.data;
    },
    create: async (eventData) => {
      const response = await apiClient.post('/events/admin', eventData);
      return response.data;
    },
    update: async (eventId, eventData) => {
      const response = await apiClient.put(`/events/admin/${eventId}`, eventData);
      return response.data;
    },
    delete: async (eventId) => {
      await apiClient.delete(`/events/admin/${eventId}`);
    },
  },
  
  // ========== Admin ==========
  admin: {
    // Customer Management
    customers: {
      list: async (params = {}) => {
        const response = await apiClient.get('/admin/customers', { params });
        return response.data;
      },
      
      approve: async (customerId, pricingTier = 'retail') => {
        const response = await apiClient.post(`/admin/customers/${customerId}/approve`, null, {
          params: { pricing_tier: pricingTier }
        });
        return response.data;
      },
      
      deny: async (customerId, reason = null) => {
        const response = await apiClient.post(`/admin/customers/${customerId}/deny`, null, {
          params: { reason }
        });
        return response.data;
      },
      
      updatePricingTier: async (customerId, pricingTier) => {
        const response = await apiClient.patch(`/admin/customers/${customerId}/pricing-tier`, null, {
          params: { pricing_tier: pricingTier }
        });
        return response.data;
      },
      
      updateNet30: async (customerId, net30Status) => {
        const response = await apiClient.patch(`/users/${customerId}/net30`, null, {
          params: { net30: net30Status }
        });
        return response.data;
      },
    },
    
    // Product Management
    products: {
      list: async (params = {}) => {
        // Params can include: page, limit, category_id, is_active, search
        const response = await apiClient.get('/admin/products', { params });
        return response.data;
      },
      
      create: async (productData) => {
        const response = await apiClient.post('/admin/products', productData);
        return response.data;
      },
      
      update: async (productId, productData) => {
        const response = await apiClient.put(`/admin/products/${productId}`, productData);
        return response.data;
      },
      
      delete: async (productId) => {
        await apiClient.delete(`/admin/products/${productId}`);
      },
      
      uploadImages: async (files) => {
        const formData = new FormData();
        // Support both single file and multiple files
        const fileArray = Array.isArray(files) ? files : [files];
        fileArray.forEach(file => {
          formData.append('files', file);
        });
        const response = await apiClient.post('/upload/product-images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
      },
      
      deleteImage: async (imageUrl) => {
        const response = await apiClient.delete('/upload/product-images', {
          params: { image_url: imageUrl }
        });
        return response.data;
      },
    },
    
    // Category Management
    categories: {
      create: async (categoryData) => {
        const response = await apiClient.post('/categories', categoryData);
        return response.data;
      },
      
      update: async (categoryId, categoryData) => {
        const response = await apiClient.put(`/categories/${categoryId}`, categoryData);
        return response.data;
      },
      
      delete: async (categoryId) => {
        await apiClient.delete(`/categories/${categoryId}`);
      },
      
      uploadImage: async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.post('/upload/category-images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
      },
      
      deleteImage: async (imageUrl) => {
        const response = await apiClient.delete('/upload/category-images', {
          params: { image_url: imageUrl }
        });
        return response.data;
      },
    },
    
    // Order Management
    orders: {
      list: async (params = {}) => {
        const response = await apiClient.get('/admin/orders', { params });
        return response.data;
      },
      
      getById: async (orderId) => {
        const response = await apiClient.get(`/admin/orders/${orderId}`);
        return response.data;
      },
      
      update: async (orderId, data) => {
        const response = await apiClient.patch(`/admin/orders/${orderId}`, data);
        return response.data;
      },
      
      updateShippingDetails: async (orderId, shippingDetails) => {
        const response = await apiClient.patch(`/admin/orders/${orderId}/shipping-details`, null, {
          params: { shipping_details: shippingDetails }
        });
        return response.data;
      },
      
      generateInvoice: async (orderId, paymentTerms = 'net30') => {
        const response = await apiClient.post(`/admin/orders/${orderId}/generate-invoice`, null, {
          params: { payment_terms: paymentTerms }
        });
        return response.data;
      },
      
      chargeSavedCard: async (orderId) => {
        const response = await apiClient.post(`/admin/orders/${orderId}/charge-saved-card`);
        return response.data;
      },
      
      checkPaymentStatus: async (paymentIntentId) => {
        const response = await apiClient.get(`/admin/payments/status/${paymentIntentId}`);
        return response.data;
      },
    },
    
    // Pricing Tier Management
    pricingTiers: {
      list: async () => {
        const response = await apiClient.get('/admin/pricing-tiers');
        return response.data;
      },
      
      create: async (tierData) => {
        const response = await apiClient.post('/admin/pricing-tiers', tierData);
        return response.data;
      },
      
      update: async (tierId, tierData) => {
        const response = await apiClient.put(`/admin/pricing-tiers/${tierId}`, tierData);
        return response.data;
      },
      
      delete: async (tierId) => {
        await apiClient.delete(`/admin/pricing-tiers/${tierId}`);
      },
    },
    
    // Reports & Analytics
    reports: {
      getSalesSummary: async (params = {}) => {
        const response = await apiClient.get('/admin/reports/sales-summary', { params });
        return response.data;
      },
      
      getCustomerStats: async () => {
        const response = await apiClient.get('/admin/reports/customer-stats');
        return response.data;
      },
      
      getInventoryStatus: async (params = {}) => {
        const response = await apiClient.get('/admin/reports/inventory-status', { params });
        return response.data;
      },

      getTrackedProductsOverview: async (params = {}) => {
        const response = await apiClient.get('/admin/reports/tracked-products', { params });
        return response.data;
      },
    },
  },

  // Expose the raw apiClient for custom requests
  apiClient,
};

export { apiClient };
export default api;

